import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import axios from '@utils/axios';
import DropdownMenu from './DropdownMenu';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// The model's beta_2 sits around 1e-4, so the axis shows multiples of 1e-5
// and the tooltip carries the full-precision values.
const BETA_AXIS_SCALE = 1e5;

const SERIES = [
  { key: 'beta2Raw', label: 'Raw', color: '#94a3b8', width: 1.5, dash: [] },
  { key: 'beta2Filtered', label: 'Filtered', color: '#2563eb', width: 2.5, dash: [] },
  { key: 'beta2Target', label: 'Target', color: '#dc2626', width: 2, dash: [8, 4] }
];

const formatBeta = (value) =>
  value === null || value === undefined ? '—' : Number(value).toExponential(3);

const formatMoisture = (value) =>
  value === null || value === undefined ? '—' : `${Number(value).toFixed(2)}%`;

// Every field the panel records per beta 2 update, in the order the CSV ships
// them. The label is the spreadsheet header; the key is the field on the record.
const EXPORT_COLUMNS = [
  { key: 'recordedAt', label: 'Date/Time' },
  { key: 'product', label: 'Product' },
  { key: 'beta2Raw', label: 'Beta 2 Raw' },
  { key: 'beta2Target', label: 'Beta 2 Target' },
  { key: 'beta2Filtered', label: 'Beta 2 Filtered' },
  { key: 'beta1', label: 'Beta 1' },
  { key: 'throughputRate', label: 'Throughput Rate' },
  { key: 'entranceMoisture', label: 'Entrance Moisture (%)' },
  { key: 'exitMoisture', label: 'Exit Moisture (%)' },
  { key: 'averageDryingTemperature', label: 'Avg Drying Temperature (C)' },
  { key: 'hotZoneTime', label: 'Hot Zone Time (s)' },
  { key: 'panelRecordId', label: 'Panel Record ID' }
];

const Beta2History = ({ deviceId }) => {
  const [data, setData] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (product) => {
    try {
      const response = await axios.get(`/v1/history/${deviceId}/beta2`, {
        params: product ? { product } : {}
      });
      setData(response.data.data);
      setSelectedProduct(response.data.data.selectedProduct);
      setError(null);
    } catch (err) {
      console.error('Error fetching beta2 history:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [deviceId]);

  useEffect(() => {
    setLoading(true);
    fetchData(null);
  }, [fetchData]);

  const handleProductSelect = (product) => {
    if (product === selectedProduct) return;
    setRefreshing(true);
    fetchData(product);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData(selectedProduct);
  };

  const records = data?.records || [];

  // The export carries what is on screen: the selected product's full series,
  // oldest first, every column. Betas are written at full precision, so a
  // spreadsheet sees the same numbers the model used.
  const exportHistory = (exportFormat) => {
    const stamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
    const base = `beta2_history_${deviceId}_${selectedProduct}_${stamp}`;
    let blob;
    let filename;

    if (exportFormat === 'csv') {
      const cell = (record, column) => {
        const value = record[column.key];
        if (value === null || value === undefined) return '';
        if (column.key === 'recordedAt') return format(new Date(value), 'yyyy-MM-dd HH:mm:ss');
        if (typeof value === 'string') return `"${value.replace(/"/g, '""')}"`;
        return value;
      };

      const content = [
        EXPORT_COLUMNS.map((column) => column.label).join(','),
        ...records.map((record) => EXPORT_COLUMNS.map((column) => cell(record, column)).join(','))
      ].join('\n');

      blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      filename = `${base}.csv`;
    } else {
      blob = new Blob([JSON.stringify(records, null, 2)], { type: 'application/json' });
      filename = `${base}.json`;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const chartData = useMemo(() => ({
    labels: records.map((_, index) => index + 1),
    datasets: SERIES.map((series) => ({
      label: series.label,
      data: records.map((record) => record[series.key]),
      borderColor: series.color,
      backgroundColor: series.color,
      borderWidth: series.width,
      borderDash: series.dash,
      pointRadius: 0,
      pointHoverRadius: 4,
      pointHitRadius: 10,
      tension: 0
    }))
  }), [records]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 400 },
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          pointStyle: 'line',
          padding: 15,
          font: { size: 11, weight: '500' }
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#ddd',
        borderWidth: 1,
        titleFont: { size: 13 },
        bodyFont: { size: 12 },
        padding: 12,
        displayColors: true,
        callbacks: {
          title: (items) => {
            const record = records[items[0]?.dataIndex];
            if (!record) return '';
            return `Record ${items[0].dataIndex + 1} • ${format(new Date(record.recordedAt), 'MMM dd, HH:mm:ss')}`;
          },
          label: (context) =>
            `${context.dataset.label}: ${formatBeta(context.parsed.y)}`,
          afterBody: (items) => {
            const record = records[items[0]?.dataIndex];
            if (!record) return [];
            const lines = [
              '',
              `Entrance moisture: ${formatMoisture(record.entranceMoisture)}`,
              `Exit moisture: ${formatMoisture(record.exitMoisture)}`,
              // The panel records hot-zone time in seconds.
              `Hot zone time: ${record.hotZoneTime == null ? '—' : `${(Number(record.hotZoneTime) / 3600).toFixed(2)} h`}`,
              `Avg drying temp: ${record.averageDryingTemperature == null ? '—' : `${Number(record.averageDryingTemperature).toFixed(1)} °C`}`,
              `Throughput rate: ${record.throughputRate == null ? '—' : Number(record.throughputRate).toFixed(1)}`,
              `Beta 1: ${formatBeta(record.beta1)}`
            ];
            return lines;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Record (oldest → newest)',
          font: { size: 11 }
        },
        ticks: {
          maxTicksLimit: 20,
          autoSkip: true,
          font: { size: 10 },
          color: '#666'
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.03)',
          drawBorder: false
        }
      },
      y: {
        title: {
          display: true,
          text: 'Beta 2 (×10⁻⁵)',
          font: { size: 11 }
        },
        ticks: {
          font: { size: 10 },
          color: '#666',
          callback: (value) => (value * BETA_AXIS_SCALE).toFixed(1)
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        }
      }
    }
  }), [records]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="flex justify-between">
          <div className="h-8 bg-gray-200 rounded w-64"></div>
          <div className="h-8 bg-gray-200 rounded w-24"></div>
        </div>
        <div className="h-80 bg-gray-100 rounded-lg"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
        <div>
          <p className="font-medium">Failed to load beta 2 history</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!data?.products?.length) {
    return (
      <div className="text-center py-12 text-gray-500">
        <ChartBarIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
        <p className="font-medium">No beta 2 history yet</p>
        <p className="text-sm mt-1">
          Rows appear here once the panel's model starts recording beta 2 updates
          and the device syncs them to the cloud.
        </p>
      </div>
    );
  }

  const activeProductInfo = data.products.find((entry) => entry.product === selectedProduct);

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {data.products.map((entry) => (
            <button
              key={entry.product}
              onClick={() => handleProductSelect(entry.product)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${
                entry.product === selectedProduct
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {entry.product}
              <span className={`ml-1.5 text-xs ${
                entry.product === selectedProduct ? 'text-blue-200' : 'text-gray-400'
              }`}>
                {entry.count}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu
            trigger={({ toggle }) => (
              <button
                onClick={toggle}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                title="Download beta 2 history"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download
              </button>
            )}
          >
            <button
              onClick={() => exportHistory('csv')}
              className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              Download as CSV
            </button>
            <button
              onClick={() => exportHistory('json')}
              className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              Download as JSON
            </button>
          </DropdownMenu>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="h-80">
        <Line data={chartData} options={chartOptions} />
      </div>

      {/* Footer */}
      {activeProductInfo && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg text-xs text-gray-600">
          <InformationCircleIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />
          <span>
            {records.length} records for <span className="capitalize font-medium">{selectedProduct}</span>
            {activeProductInfo.oldestRecord && activeProductInfo.newestRecord && (
              <>
                {' '}spanning {format(new Date(activeProductInfo.oldestRecord), 'MMM dd HH:mm')}
                {' – '}{format(new Date(activeProductInfo.newestRecord), 'MMM dd HH:mm')}
              </>
            )}
            . Hover any point for the packet details behind that calculation.
          </span>
        </div>
      )}
    </div>
  );
};

export default Beta2History;
